/* eslint-disable react-hooks/exhaustive-deps */
import { Col, ColProps } from 'antd';
import { memo, useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import FormItemWrap from '@/components/FormItemWrap';
import SelectWrap from '@/components/SelectWrap';
import commonService from '@/services/common.service';
import { Role } from '@/types/roles';
import { handleMessageError } from '@/util/handleMessageError';

interface IProps {
    control?: any;
    setValue?: any;
    propsCol?: any;
    labelCol?: ColProps;
    labelAlign?: 'left' | 'right';
    errors?: any;
    clearErrors?: any;
    disabled?: boolean;
    required?: boolean;
    findAllStore?: boolean;
    role?: any;
}

const FranchiseStoreFilter: React.FC<IProps> = ({
    clearErrors,
    control,
    errors,
    setValue,
    propsCol,
    labelCol,
    labelAlign,
    disabled,
    required = true,
    findAllStore = false,
    role,
}) => {
    const { t } = useTranslation();

    const { data: franchises } = useQuery('franchise-options', commonService.getFranchises, {
        keepPreviousData: true,
        onError: (error: any) => {
            handleMessageError(error, t);
        },
    });

    const franchiseId = useWatch({
        control,
        name: 'franchiseId',
    });

    const fcOptions =
        (franchises?.data?.length &&
            franchises?.data.map((value: any) => ({ id: String(value.id), value: value.name }))) ||
        [];

    const currentFranchise = franchises?.data?.length && franchises?.data.find((f: any) => f.id === franchiseId);
    const storeOptions =
        (currentFranchise?.store?.length &&
            currentFranchise.store.map((value: any) => ({ id: String(value.id), value: value.name }))) ||
        [];
    if (findAllStore) {
        if (franchiseId && storeOptions?.length) {
            storeOptions.unshift({
                id: 'all',
                value: t('common:ALL'),
            });
        }
    }

    useEffect(() => {
        if (fcOptions?.length === 1) {
            setValue('franchiseId', fcOptions[0].id);
        }
    }, [franchises]);

    return (
        <>
            <Col {...propsCol}>
                <FormItemWrap
                    labelAlign={labelAlign}
                    label={t('FC_NAME')}
                    validateStatus={errors?.franchiseId ? 'error' : ''}
                    help={
                        String(errors?.franchiseId?.type) === 'required' &&
                        t('VALIDATION.REQUIRED_SELECT', { name: t('FC_NAME') })
                    }
                    required={required}
                    labelCol={labelCol}
                >
                    <Controller
                        name="franchiseId"
                        rules={{ required }}
                        control={control}
                        render={({ field: { onChange, ...field } }) => (
                            <SelectWrap
                                onChange={(value: string) => {
                                    setValue('storeId', '');
                                    setValue('staffId', '');
                                    onChange(value);
                                    clearErrors && clearErrors('franchiseId');
                                }}
                                allowClear
                                options={fcOptions}
                                field={field}
                                disabled={
                                    disabled ||
                                    (role && [Role.HEAD, Role.REMOTE_STAFF, Role.REMOTE_MANAGER].includes(role))
                                }
                            />
                        )}
                    />
                </FormItemWrap>
            </Col>
            <Col {...propsCol}>
                <FormItemWrap
                    labelAlign={labelAlign}
                    label={t('STORE_NAME')}
                    labelCol={labelCol}
                    validateStatus={errors?.storeId ? 'error' : ''}
                    help={
                        String(errors?.storeId?.type) === 'required' &&
                        t('VALIDATION.REQUIRED_SELECT', { name: t('STORE_NAME') })
                    }
                    required={required}
                >
                    <Controller
                        name="storeId"
                        rules={{ required }}
                        control={control}
                        render={({ field: { onChange, ...field } }) => (
                            <SelectWrap
                                allowClear
                                onChange={(storeId: string) => {
                                    onChange(storeId);
                                    setValue('staffId', '');
                                    setValue('storeName', storeOptions.find((item: any) => item.id === storeId)?.value);
                                    localStorage.setItem(
                                        'storeName',
                                        storeOptions.find((item: any) => item.id === storeId)?.value,
                                    );
                                    clearErrors && clearErrors('storeId');
                                }}
                                // options={findAllStore ? newStoreOptions : storeOptions}
                                options={storeOptions}
                                field={field}
                                disabled={
                                    disabled ||
                                    (role &&
                                        [Role.HEAD, Role.FC, Role.REMOTE_STAFF, Role.REMOTE_MANAGER].includes(role))
                                }
                                className="fit-content"
                            />
                        )}
                    />
                </FormItemWrap>
            </Col>
        </>
    );
};

export default memo(FranchiseStoreFilter);
